import { isAdmin, useAuth } from '@/lib/auth';
import { cn } from '@jeraisonne/ui';
import { useRoleNewsFeed } from '@/lib/news/use-role-news-feed';
import { useFeedVisit } from '@/lib/news/feed-seen';
import { isFeedItemUnread } from '@/lib/news/feed-unread';
import { useAdminFeedAll } from '@/lib/news/admin-feed-scope';
import {
  useAdminFeedHideInternal,
  useInternalAccountIds,
  withoutInternal,
} from '@/lib/news/feed-internal';
import { NewsFeed } from '@/components/news/news-feed';
import { CreatePrivateLiveLink } from '@/features/private-live/private-lives-section';
import { FeedRefreshButton } from '@/features/news-refresh/feed-refresh-button';
import { PullToRefreshZone } from '@/features/news-refresh/pull-to-refresh-zone';
import { useFeedAutoRefresh } from '@/features/news-refresh/use-feed-auto-refresh';
import type { FeedItem } from '@/components/news/types';

export function TutorNewsPage() {
  const { user, profile } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const [allUsers, setAllUsers] = useAdminFeedAll();
  const everyone = admin && allUsers;
  const feed = useRoleNewsFeed('tutor', { allUsers: everyone });
  const {
    authorsById,
    doneAssignmentKeys,
    isLoading,
    hasMore,
    isFetchingMore,
    loadMore,
    refresh,
    isRefreshing,
  } = feed;
  // Admin : activités des comptes internes et de test masquées (réglage de l'appareil).
  const [hideInternal, setHideInternal] = useAdminFeedHideInternal();
  const internalIds = useInternalAccountIds(admin && hideInternal);
  const items =
    admin && hideInternal && internalIds.data
      ? withoutInternal(feed.items, internalIds.data, user?.id)
      : feed.items;
  const seenAtAtOpen = useFeedVisit();
  const isUnread = (item: FeedItem) =>
    seenAtAtOpen !== undefined && isFeedItemUnread(item, seenAtAtOpen, user?.id);

  useFeedAutoRefresh(refresh);

  return (
    <div className="pb-safe mx-auto max-w-2xl px-4 py-6 sm:py-8">
      <div className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <h1 className="font-display text-ink-900 text-2xl font-extrabold">Feed</h1>
          <FeedRefreshButton onRefresh={refresh} isRefreshing={isRefreshing} />
        </div>
        <p className="text-ink-500 mt-1 font-sans text-sm">
          {everyone
            ? 'Toutes les activités de tous les comptes (vue admin).'
            : 'Devoirs donnés, exercices reçus, défis et résultats de tes élèves liés.'}
        </p>
        <CreatePrivateLiveLink className="mt-3" />
        {admin && (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <AdminScopeSwitch checked={everyone} onChange={setAllUsers} />
            <AdminScopeSwitch
              checked={hideInternal}
              onChange={setHideInternal}
              label="Masquer les comptes internes"
              hint="@jeraisonne.fr, admins et comptes de test"
            />
          </div>
        )}
      </div>

      <PullToRefreshZone onRefresh={refresh} isRefreshing={isRefreshing}>
        <NewsFeed
          items={items}
          isLoading={isLoading}
          hasMore={hasMore}
          isFetchingMore={isFetchingMore}
          onLoadMore={loadMore}
          viewerRole="tutor"
          viewerId={user?.id}
          authorsById={authorsById}
          doneAssignmentKeys={doneAssignmentKeys}
          isUnread={isUnread}
        />
      </PullToRefreshZone>
    </div>
  );
}

/** Interrupteur admin du Feed (« Tout le monde » par défaut). */
export function AdminScopeSwitch({
  checked,
  onChange,
  className,
  label = 'Tout le monde (admin)',
  hint = 'Activités de tous les comptes',
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  className?: string;
  label?: string;
  hint?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'border-ink-200 flex min-h-[2.75rem] w-full items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-2 text-left active:scale-[0.99] sm:w-auto',
        className,
      )}
    >
      <span className="min-w-0">
        <span className="text-ink-900 block text-sm font-semibold">{label}</span>
        <span className="text-ink-500 block text-xs">{hint}</span>
      </span>
      <span
        aria-hidden
        className={cn(
          'relative h-6 w-11 shrink-0 rounded-full transition-colors',
          checked ? 'bg-primary-500' : 'bg-ink-200',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-5 w-5 rounded-full bg-[#ffffff] shadow transition-transform',
            checked ? 'translate-x-[22px]' : 'translate-x-0.5',
          )}
        />
      </span>
    </button>
  );
}
